import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Plus, Search, Pencil, Trash2, Users, Key, Link2, Download } from 'lucide-react';
import { toast } from 'sonner';
import SetPasswordDialog from '../../components/SetPasswordDialog';

const defaultForm = { first_name: '', last_name: '', email: '', phone: '', subjects: [], qualification: '' };

export default function TeacherManagement() {
  const [teachers, setTeachers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [search, setSearch] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [editing, setEditing] = useState(null);
  const [subjectInput, setSubjectInput] = useState('');
  const [pwUser, setPwUser] = useState(null);
  const [showClassAssign, setShowClassAssign] = useState(null);
  const [assignedClasses, setAssignedClasses] = useState([]);

  const load = () => {
    const params = search ? `?search=${search}` : '';
    api.get(`/admin/teachers${params}`).then(r => setTeachers(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [search]);
  useEffect(() => { api.get('/admin/classes').then(r => setClasses(r.data)).catch(() => {}); }, []);

  const openClassAssign = (t) => {
    setShowClassAssign(t);
    setAssignedClasses(t.class_ids || []);
  };

  const saveClassAssignment = async () => {
    try {
      await api.put(`/admin/teachers/${showClassAssign.id}/assign-classes`, { class_ids: assignedClasses });
      toast.success('Classes assigned');
      setShowClassAssign(null);
      load();
    } catch { toast.error('Failed to assign classes'); }
  };

  const handleSave = async () => {
    try {
      if (editing) {
        await api.put(`/admin/teachers/${editing}`, form);
        toast.success('Teacher updated');
      } else {
        await api.post('/admin/teachers', form);
        toast.success('Teacher created');
      }
      setShowDialog(false); setForm(defaultForm); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleEdit = (t) => {
    setForm({ first_name: t.first_name, last_name: t.last_name, email: t.email, phone: t.phone || '', subjects: t.subjects || [], qualification: t.qualification || '' });
    setEditing(t.id); setShowDialog(true);
  };

  const addSubject = () => {
    if (subjectInput.trim()) {
      setForm({ ...form, subjects: [...form.subjects, subjectInput.trim()] });
      setSubjectInput('');
    }
  };

  return (
    <div data-testid="teacher-management">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#1B2A4A] flex items-center gap-2"><Users className="w-6 h-6" /> Teachers</h1>
          <p className="text-sm text-slate-500">{teachers.length} active teachers</p>
        </div>
        <Button onClick={() => { setForm(defaultForm); setEditing(null); setShowDialog(true); }} className="bg-[#1B2A4A] hover:bg-[#283E6B]" data-testid="add-teacher-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Teacher
        </Button>
        <Button variant="outline" className="border-[#1B2A4A] text-[#1B2A4A]" onClick={() => { const token = localStorage.getItem('token'); fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/teachers/export`, { headers: { Authorization: `Bearer ${token}` } }).then(r=>r.blob()).then(b=>{ const a=document.createElement('a'); a.href=URL.createObjectURL(b); a.download='teachers_export.csv'; a.click(); }); }} data-testid="export-teachers-csv">
          <Download className="w-4 h-4 mr-1" /> Export CSV
        </Button>
      </div>
      <div className="relative mb-6">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input placeholder="Search teachers..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 max-w-md" data-testid="search-teachers-input" />
      </div>
      <div className="bg-white rounded-xl border shadow-sm">
        <Table>
          <TableHeader><TableRow className="data-table">
            <TableHead>Name</TableHead><TableHead>Employee ID</TableHead><TableHead>Email</TableHead><TableHead>Subjects</TableHead><TableHead>Classes</TableHead><TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {teachers.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">No teachers found</TableCell></TableRow>
            ) : teachers.map(t => (
              <TableRow key={t.id}>
                <TableCell className="font-semibold text-[#1B2A4A]">{t.first_name} {t.last_name}</TableCell>
                <TableCell><Badge variant="outline" className="font-mono text-xs">{t.employee_id}</Badge></TableCell>
                <TableCell className="text-sm text-slate-500">{t.email}</TableCell>
                <TableCell><div className="flex flex-wrap gap-1">{(t.subjects||[]).map(s => <Badge key={s} className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">{s}</Badge>)}</div></TableCell>
                <TableCell><div className="flex flex-wrap gap-1">{(t.class_ids||[]).map(cid => { const c = classes.find(cl=>cl.id===cid); return <Badge key={cid} variant="outline" className="text-xs">{c?.name||cid}</Badge>; })}{(t.class_ids||[]).length===0 && <span className="text-xs text-slate-400">None</span>}</div></TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" title="Assign Classes" onClick={() => openClassAssign(t)} data-testid={`assign-classes-${t.id}`}><Link2 className="w-4 h-4 text-sky-600" /></Button>
                  <Button variant="ghost" size="icon" title="Set Password" onClick={() => setPwUser({ id: t.user_id, name: `${t.first_name} ${t.last_name}`, email: t.email })} data-testid={`set-password-teacher-${t.id}`}><Key className="w-4 h-4 text-amber-600" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => handleEdit(t)}><Pencil className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={async () => { if(window.confirm('Deactivate?')){ await api.delete(`/admin/teachers/${t.id}`); toast.success('Deactivated'); load(); }}}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? 'Edit Teacher' : 'Add Teacher'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>First Name</Label><Input value={form.first_name} onChange={e => setForm({...form, first_name: e.target.value})} data-testid="teacher-first-name" /></div>
            <div><Label>Last Name</Label><Input value={form.last_name} onChange={e => setForm({...form, last_name: e.target.value})} data-testid="teacher-last-name" /></div>
            <div className="col-span-2"><Label>Email</Label><Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="teacher-email" /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} /></div>
            <div><Label>Qualification</Label><Input value={form.qualification} onChange={e => setForm({...form, qualification: e.target.value})} /></div>
            <div className="col-span-2">
              <Label>Subjects</Label>
              <div className="flex gap-2 mt-1">
                <Input value={subjectInput} onChange={e => setSubjectInput(e.target.value)} placeholder="Add subject" onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addSubject())} />
                <Button type="button" variant="outline" onClick={addSubject}>Add</Button>
              </div>
              <div className="flex flex-wrap gap-1 mt-2">{form.subjects.map((s,i) => (
                <Badge key={i} className="bg-slate-100 text-slate-700 cursor-pointer" onClick={() => setForm({...form, subjects: form.subjects.filter((_,j)=>j!==i)})}>{s} &times;</Badge>
              ))}</div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} className="bg-[#1B2A4A]" data-testid="save-teacher-btn">{editing ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Class Assignment Dialog */}
      <Dialog open={!!showClassAssign} onOpenChange={() => setShowClassAssign(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Assign Classes to {showClassAssign?.first_name} {showClassAssign?.last_name}</DialogTitle></DialogHeader>
          <div className="max-h-60 overflow-y-auto border rounded-lg p-2 space-y-1">
            {classes.length === 0 ? <p className="text-xs text-slate-400 p-2">No classes available</p> :
              classes.map(c => (
                <label key={c.id} className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-slate-50 ${assignedClasses.includes(c.id) ? 'bg-blue-50' : ''}`}>
                  <input type="checkbox" checked={assignedClasses.includes(c.id)} onChange={() => setAssignedClasses(prev => prev.includes(c.id) ? prev.filter(id=>id!==c.id) : [...prev, c.id])} className="rounded" />
                  <span className="text-sm">{c.name} <span className="text-xs text-slate-400">({c.division} - {c.grade})</span></span>
                </label>
              ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowClassAssign(null)}>Cancel</Button>
            <Button onClick={saveClassAssignment} className="bg-[#1B2A4A]" data-testid="save-class-assignment-btn">Save Assignment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Set Password Dialog */}
      {pwUser && <SetPasswordDialog open={!!pwUser} onOpenChange={() => setPwUser(null)} userId={pwUser.id} userName={pwUser.name} userEmail={pwUser.email} />}
    </div>
  );
}
